import { useState } from "react";
import { CheckCircle, WhatsappLogo, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { naira } from "@/lib/format";
import { OpBadge, PayBadge } from "@/components/StatusBadge";
import DeliveryTimeline from "@/components/DeliveryTimeline";

// Reusable quotation / order summary with payment psychology.
// props: order, onAccept, onPay(amount), busy, shareLink
export default function OrderSummary({ order, onAccept, onPay, busy, shareLink }) {
  const f = order.financials;
  const category = order.payment_category;
  const suggested =
    f.payment_status === "Fully Paid"
      ? 0
      : Math.max(f.required_before_dispatch - f.amount_paid, f.outstanding > 0 && f.required_before_dispatch === 0 ? f.outstanding : 0) ||
        (f.required_before_dispatch - f.amount_paid > 0 ? f.required_before_dispatch - f.amount_paid : f.outstanding);
  const [amount, setAmount] = useState(String(Math.max(0, Math.round(suggested))));

  const canAccept = order.operational_status === "Quote Sent";
  const quoted = f.total > 0 && order.operational_status !== "Quote Requested";
  const showPay = ["Quote Accepted", "Awaiting Payment", "Payment Confirmed", "Scheduled",
    "Preparing for Dispatch", "Dispatched", "In Transit", "Delivered"].includes(order.operational_status)
    && f.outstanding > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden">
        <div className="bg-[#0A4F35] px-6 py-5 flex items-center justify-between">
          <div>
            <p className="text-emerald-100/70 text-xs uppercase tracking-widest">Order</p>
            <h2 className="font-display text-2xl font-bold text-white" data-testid="summary-order-number">{order.order_number}</h2>
          </div>
          <div className="text-right">
            <OpBadge status={order.operational_status} />
          </div>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <Row label="Company" value={order.company_name} />
          <Row label="Contact" value={order.contact_name || "—"} />
          <Row label="Fuel" value={order.fuel_type} className="capitalize" />
          <Row label="Quantity" value={`${Number(order.litres).toLocaleString()} litres`} />
          <Row label="Delivery address" value={order.delivery_address || "—"} />
          <Row label="Requested date" value={order.requested_date || "—"} />
          {order.po_reference ? <Row label="PO / Ref" value={order.po_reference} /> : null}
        </div>
      </div>

      {/* Pricing */}
      {quoted ? (
        <div className="rounded-lg border border-[#E5E7EB] bg-white p-6">
          <h3 className="font-display font-semibold text-slate-900">Quotation</h3>
          <div className="mt-4 space-y-2 text-sm">
            <LineRow label={`Fuel (${Number(order.litres).toLocaleString()} L × ${naira(order.price_per_litre)})`} value={naira(f.subtotal)} />
            {order.delivery_charge > 0 && <LineRow label="Delivery charge" value={naira(order.delivery_charge)} />}
            {order.tax_amount > 0 && <LineRow label="Tax" value={naira(order.tax_amount)} />}
            <div className="border-t border-[#E5E7EB] my-2" />
            <LineRow label="Total" value={naira(f.total)} bold />
            {f.amount_paid > 0 && <LineRow label="Paid to date" value={`− ${naira(f.amount_paid)}`} />}
          </div>
          {order.quote_notes ? <p className="mt-4 text-sm text-slate-500 italic">{order.quote_notes}</p> : null}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-[#E5B800] bg-[#FBF3D5]/40 p-6 text-center">
          <p className="text-sm text-[#7A5C00] font-medium">Your quotation is being prepared. We'll notify you when it's ready to accept.</p>
        </div>
      )}

      {/* Payment psychology block */}
      {quoted && (
        <div className="rounded-lg border-2 border-[#0A4F35]/20 bg-white p-6" data-testid="payment-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-slate-900">Payment</h3>
            <PayBadge status={f.payment_status} />
          </div>

          {f.payment_status === "Fully Paid" ? (
            <div className="mt-4 flex items-center gap-2 text-[#16A34A] font-semibold">
              <CheckCircle size={22} weight="fill" /> Payment complete — thank you.
            </div>
          ) : (
            <>
              <div className="mt-4 grid gap-2 text-sm">
                <LineRow label="Order total" value={naira(f.total)} />
                {category === "deposit" && f.amount_paid < f.deposit_required && (
                  <>
                    <LineRow label={`Deposit required (${f.deposit_percent}%)`} value={naira(f.deposit_required)} highlight />
                    <LineRow label="Outstanding after deposit" value={naira(f.total - f.deposit_required)} />
                  </>
                )}
                {category === "new_standard" && (
                  <LineRow label="Required before dispatch" value={naira(f.required_before_dispatch)} highlight />
                )}
                <LineRow label="Outstanding balance" value={naira(f.outstanding)} bold />
              </div>

              {canAccept ? (
                <>
                  <Button data-testid="accept-quote-btn" onClick={onAccept} disabled={busy}
                    className="mt-5 w-full h-12 bg-[#0A4F35] hover:bg-[#083D29] text-white font-bold text-base">
                    Accept Quote
                  </Button>
                  <p className="mt-2 text-center text-xs text-slate-500">Accept to unlock payment and schedule your delivery.</p>
                </>
              ) : showPay ? (
                <div className="mt-5">
                  <div className="flex gap-2">
                    <Input data-testid="pay-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12" />
                    <Button data-testid="pay-now-btn" onClick={() => onPay(Number(amount))} disabled={busy || Number(amount) <= 0}
                      className="h-12 px-6 bg-[#E5B800] hover:bg-[#CCA300] text-black font-bold whitespace-nowrap">
                      {category === "deposit" && f.amount_paid < f.deposit_required ? "Pay Deposit" : "Pay Now"}
                    </Button>
                  </div>
                  <p className="mt-2 text-center text-xs text-slate-500">
                    Your delivery will be scheduled once payment is confirmed. (Simulated payment for demo)
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}

      {/* Delivery timeline */}
      {order.operational_status !== "Quote Requested" && order.operational_status !== "Quote Sent" && (
        <div className="rounded-lg border border-[#E5E7EB] bg-white p-6">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Delivery Progress</h3>
          <DeliveryTimeline steps={order.delivery_progress} />
        </div>
      )}

      {/* Share link */}
      {shareLink && (
        <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-4 flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid="copy-share-btn"
            onClick={() => { navigator.clipboard.writeText(shareLink); toast.success("Link copied"); }}>
            <Copy size={16} className="mr-1" /> Copy link
          </Button>
          <a href={`https://wa.me/?text=${encodeURIComponent("Your RODAIR quotation is ready. View and confirm here: " + shareLink)}`}
            target="_blank" rel="noreferrer">
            <Button size="sm" className="bg-[#16A34A] hover:bg-[#128a3d] text-white">
              <WhatsappLogo size={16} weight="fill" className="mr-1" /> Share on WhatsApp
            </Button>
          </a>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, className = "" }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
      <div className={`text-slate-800 font-medium ${className}`}>{value}</div>
    </div>
  );
}

function LineRow({ label, value, bold, highlight }) {
  return (
    <div className={`flex items-center justify-between ${highlight ? "text-[#0A4F35]" : "text-slate-600"}`}>
      <span className={bold ? "font-semibold text-slate-900" : ""}>{label}</span>
      <span className={`tabular-nums ${bold ? "font-display font-extrabold text-lg text-slate-900" : highlight ? "font-bold" : ""}`}>{value}</span>
    </div>
  );
}
